import cc from "classnames"
import type { ReactNode, MouseEvent } from "react"

import Loader from "@components/Loader/Loader"

import { ReactComponent as TrashCan } from "@icons/trash-can.svg"
import { ReactComponent as Plus } from "@icons/plus.svg"
import { ReactComponent as ChevronRight } from "@icons/chevron-right.svg"

export const BASE_CLASSNAMES =
  "font-semibold inline-flex items-center whitespace-nowrap justify-center transition-colors relative duration-300 group"

export const VERSIONS: Record<ButtonVersion, string> = {
  PRIMARY: "bg-black text-white hover:bg-black-hover",
  SPINACH: "bg-spinach text-white hover:bg-spinach-hover",
  SECONDARY: "border border-neutral-200 bg-white hover:border-neutral-300",
  SECONDARY_TRANSPARENT: "border border-neutral-200 bg-transparent hover:border-neutral-300",
  DASHED: "border border-dashed border-neutral-300 text-neutral-300 hover:border-neutral-300",
  BACK: "border border-neutral-200 bg-white bg-white text-black hover:border-neutral-300",
  FORWARD: "bg-black text-white hover:bg-black-hover flex justify-between",
  ADD: "border border-dashed border-neutral-200 bg-white text-neutral-300 hover:border-neutral-300",
  DELETE: "border border-neutral-200 bg-white text-red hover:border-neutral-300",
  OVERLAY_DARK: "bg-black text-white shadow-soft hover:border-neutral-300",
  OVERLAY_LIGHT: "bg-white text-black shadow-soft",
  ROUNDED: "rounded-full bg-white text-black",
  NONE: "appearance-none",
}
export const SIZES: Record<ButtonSize, string> = {
  SMALL: "h-32 px-12 text-14 rounded-8",
  MEDIUM: "h-48 px-24 text-base rounded-16",
  LARGE: "h-64 px-24 text-base rounded-16",
  NEWSLETTER: "p-12 text-base rounded-4 border-spinach border hover:border-spinach-hover",
  SMALL_SQUARE: "h-32 w-32 text-14 rounded-8",
  MEDIUM_SQUARE: "h-48 w-48 text-base rounded-16",
  LARGE_SQUARE: "h-64 w-64 text-base rounded-16",
  SMALL_ROUND: "h-40 w-40",
  LINK: "",
  LINK_UNDERLINED: "underline",
}

export type ButtonVersion =
  | "PRIMARY"
  | "SECONDARY"
  | "SECONDARY_TRANSPARENT"
  | "SPINACH"
  | "DASHED"
  | "BACK"
  | "FORWARD"
  | "ADD"
  | "DELETE"
  | "OVERLAY_DARK"
  | "OVERLAY_LIGHT"
  | "ROUNDED"
  | "NONE"

export type ButtonSize =
  | "MEDIUM"
  | "SMALL"
  | "LARGE"
  | "NEWSLETTER"
  | "SMALL_SQUARE"
  | "MEDIUM_SQUARE"
  | "LARGE_SQUARE"
  | "SMALL_ROUND"
  | "LINK"
  | "LINK_UNDERLINED"

export interface ButtonProps {
  children?: ReactNode
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void
  version: ButtonVersion
  size: ButtonSize
  type?: "button" | "submit" | "reset"
  class_name?: string
  is_loading?: boolean
  is_disabled?: boolean
  is_block?: boolean
  "data-cy"?: string
}

function Button(props: ButtonProps): JSX.Element {
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (props.is_disabled || typeof props.onClick !== "function") {
      return
    }
    props.onClick(e)
  }

  return (
    <button
      onClick={handleClick}
      type={props.type}
      aria-busy={props.is_loading}
      data-cy={props["data-cy"]}
      className={cc(VERSIONS[props.version], SIZES[props.size], {
        [BASE_CLASSNAMES]: props.version !== "NONE",
        [String(props.class_name)]: Boolean(props.class_name),
        "opacity-60": props.is_disabled,
        "w-full": props.is_block,
        "pointer-events-none": props.is_loading || props.is_disabled,
      })}
    >
      {props.is_loading ? (
        <>                      
          <Loader  version="BUTTON"/>
          <span aria-disabled className="invisible">
            {props.children}
          </span>
        </>
      ) : (
        <>
          {props.size === "SMALL_ROUND" && (
            <div className="w-full h-full bg-white z-0 absolute left-0 top-0 rounded-full hover:scale-110 transition-all duration-150 ease-out"></div>
          )}
          {props.version === "DELETE" && (
            <div className="w-16 mr-8">
              <TrashCan />
            </div>
          )}
          {props.version === "ADD" && (
            <div className="w-16 mr-8">
              <Plus />
            </div>
          )}
          {props.version === "BACK" && (
            <div className="w-16">
              <ChevronRight className="transform rotate-180" />
            </div>
          )}
          <div className="contents">{props.children}</div>
          {props.version === "FORWARD" && (
            <div className="w-16">
              <ChevronRight />
            </div>
          )}
        </>
      )}
    </button>
  )
}

Button.defaultProps = {
  version: "DARK",
  type: "button",
  size: "MEDIUM",
}

export default Button
